/**
 * M7 S32: "Your records" (spec §2.4, C1), generated from the Career and laid out like the Column Nine manifests (a
 * Day column first, one row per entry, no totals):
 *
 * - tetherstone sales (`career.tetherSales`: day, tonnes, price, station) with Voss's countersignature line;
 * - kills (`career.guardianKills`: day, system, hull), logged as "raider" until tier 3, then "guardian";
 * - the Holdfast log for the pilot Reyes names (`watch.reyes_named_pilot`, a `yourRecords` log the ledger holds);
 * - resonance meter readings (`story.meterProofs`, from Watchers);
 * - the player corporation's tetherstone share (the M4 worker's per-career record, passed in by the story link);
 * - stations handed over to Tethys (`story.reactivations`, `handedOver` to `tethys`).
 *
 * No counters anywhere: rows are records, never tallies ("17/24" is banned).
 */
import { guardianKillsOf, tetherSalesOf } from '../../../career/logs'
import type { Career } from '../../../career/types'
import { currentTier } from '../../../story/claims'
import { storyOf } from '../../../story/state'
import { formatCr } from '../../format'
import type { LogsConfig } from './config'
import type { LogEntryView } from './logs'

/** Display names (the story link passes the Reach's). */
export interface LogsNames {
  system(id: string): string
  station(id: string): string
  hull(id: string): string
  poi(id: string): string
}

/** What the Career doesn't hold, passed in by the story link. */
export interface RecordsExtras {
  /** The player corporation's tetherstone sold to Tethys refineries (null or absent: no corporation). */
  corpShare?: { corp: string; tonnes: number } | null
}

export interface RecordTable {
  id: 'sales' | 'kills' | 'meter' | 'handedOver'
  title: string
  columns: readonly string[]
  rows: readonly (readonly string[])[]
  caption: string | null
}

export interface RecordsView {
  tables: readonly RecordTable[]
  /** One-line records (the corporation's share). */
  lines: readonly { title: string; text: string }[]
  /** Generated `yourRecords` logs in the ledger (the named pilot's message). */
  logs: readonly LogEntryView[]
  empty: boolean
}

function tonnesText(t: number): string {
  return Number.isInteger(t) ? String(t) : t.toFixed(1)
}

export function yourRecords(
  career: Career,
  cfg: LogsConfig,
  names: LogsNames,
  extras: RecordsExtras,
  logs: readonly LogEntryView[],
): RecordsView {
  const r = cfg.records
  const day = (d: number): string => `${r.dayPrefix}${d}`
  const tables: RecordTable[] = []

  const sales = tetherSalesOf(career)
  if (sales.length > 0) {
    tables.push({
      id: 'sales',
      title: r.sales.title,
      columns: r.sales.columns,
      rows: sales.map((s) => [day(s.day), tonnesText(s.tonnes), formatCr(s.credits), names.station(s.stationId)]),
      caption: r.sales.signed,
    })
  }

  const kills = guardianKillsOf(career)
  if (kills.length > 0) {
    const label = currentTier(career) >= 3 ? r.kills.labelLate : r.kills.labelEarly
    tables.push({
      id: 'kills',
      title: r.kills.title,
      columns: r.kills.columns,
      rows: kills.map((k) => [
        day(k.day),
        names.system(k.systemId),
        k.hull === undefined ? r.kills.unknownHull : names.hull(k.hull),
        k.selfDefence === true ? `${label}, ${r.kills.selfDefence}` : label,
      ]),
      caption: null,
    })
  }

  const s = storyOf(career)
  const meter = s.meterProofs ?? []
  if (meter.length > 0) {
    tables.push({
      id: 'meter',
      title: r.meter.title,
      columns: r.meter.columns,
      rows: meter.map((m) => [day(m.day), m.opId, r.meter.reading]),
      caption: null,
    })
  }

  const handed = s.reactivations.filter((x) => x.state === 'handedOver' && x.recipient === 'tethys')
  if (handed.length > 0) {
    tables.push({
      id: 'handedOver',
      title: r.handedOver.title,
      columns: r.handedOver.columns,
      rows: handed.map((x) => [day(x.lastLitDay), names.poi(x.poiId), r.handedOver.recipient]),
      caption: null,
    })
  }

  const lines: { title: string; text: string }[] = []
  const corp = extras.corpShare
  if (corp !== undefined && corp !== null && corp.tonnes > 0) {
    lines.push({
      title: r.corp.title,
      text: r.corp.line.replace('{corp}', corp.corp).replace('{tonnes}', tonnesText(corp.tonnes)),
    })
  }

  return { tables, lines, logs, empty: tables.length === 0 && lines.length === 0 && logs.length === 0 }
}
